import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  IconButton,
  Chip,
  Skeleton
} from '@mui/material';
import {
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Notification } from '../../services/api/notifications/notificationsApi';
import NotificationItem from './NotificationItem';
import NotificationModal from './NotificationModal';

interface NotificationDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  onMarkAsRead: (notificationId: number) => void;
  onMarkAsUnread: (notificationId: number) => void;
  onMarkAllAsRead: () => void;
  onRefresh: () => void;
  onClose: () => void;
}

/**
 * Componente do dropdown de notificações
 * Mostra as 5 notificações mais recentes e permite abrir modal para ver todas
 */
const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  isLoading,
  onMarkAsRead,
  onMarkAsUnread,
  onMarkAllAsRead,
  onRefresh,
  onClose
}) => {
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const hasNotifications = notifications.length > 0;
  const hasUnreadNotifications = unreadCount > 0;

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);    
    onClose();
  };

  const handleMarkAllAsRead = () => {
    onMarkAllAsRead();
  };

  if (isLoading) {
    return (
      <Box sx={{ width: 480, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Skeleton variant="text" width={120} height={24} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
        <List sx={{ p: 0 }}>
          {[1, 2, 3].map((item) => (
            <ListItem key={item} sx={{ px: 0, py: 1 }}>
              <Box sx={{ width: '100%' }}>
                <Skeleton variant="text" width="100%" height={20} />
                <Skeleton variant="text" width="80%" height={16} />
                <Skeleton variant="text" width="60%" height={14} />
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 560 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight="bold">
            Notificações
          </Typography>
          {hasUnreadNotifications && (
            <Chip 
              label={unreadCount} 
              size="small" 
              color="error"
              sx={{ minWidth: 24, height: 20, fontSize: '0.75rem' }}
            />
          )}
        </Box>
        
        <Box>
          <IconButton 
            size="small" 
            onClick={onRefresh}
            title="Atualizar"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={onClose}
            title="Fechar"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      {!hasNotifications ? (
        <Box 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            color: theme.palette.text.secondary
          }}
        >
          <Typography variant="body2">
            Nenhuma notificação encontrada
          </Typography>
        </Box>
      ) : (
        <>
          {/* Notifications List */}
          <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
            {notifications.slice(0, 5).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={onMarkAsRead}
                  onMarkAsUnread={onMarkAsUnread}
                  onClose={onClose}
                />
                {index < Math.min(notifications.length - 1, 4) && <Divider />}
              </React.Fragment>
            ))}
          </List>

          {/* Footer Actions */}
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.grey[50]
            }}
          >
            <Box display="flex" gap={1} justifyContent="space-between">
              {hasUnreadNotifications && (
                <Button
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  onClick={handleMarkAllAsRead}
                  variant="outlined"
                  sx={{ flex: 1 }}
                >
                  Marcar todas como lidas
                </Button>
              )}
              
              <Button
                size="small"
                endIcon={<LaunchIcon />}
                onClick={handleOpenModal}
                variant="contained"
                sx={{ flex: hasUnreadNotifications ? 1 : 'auto' }}
              >
                Ver todas
              </Button>
            </Box>
          </Box>
        </>
      )}

      {/* Modal para ver todas as notificações */}
      <NotificationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onMarkAsRead={onMarkAsRead}
        onMarkAsUnread={onMarkAsUnread}
        onMarkAllAsRead={onMarkAllAsRead}
        onRefresh={onRefresh}
      />
    </Box>
  );
};

export default NotificationDropdown;
